import React, {memo, useContext, useMemo} from 'react';

import {NumberInput} from 'src/components/input';
import {MAX_NUMBER_VALUE} from 'src/constants';

import {Integer} from '@pb/kit-component-formatters';
import {LocaleContext} from '@pb/kit-context-locale';

import {LimitItemLabel} from './limit-item-label';

import type {DictionaryItem} from 'src/openapi/admin/models/DictionaryItem';

type OnChangeLimits = (code: string, value?: number) => void;


type Props = {
    limitId: string,
    limitDictionary: DictionaryItem,
    onChangeLimit: OnChangeLimits,
    onClearTemporaryLimit?: (code: string) => void,
    onClearCustomLimit: (code: string) => void,

    usedValue?: number,
    defaultValue: number,
    customValue?: number,
    tempValue?: number,
    untilDate?: string,

    readonly?: boolean,
    labelExtra?: React.ReactNode,
};

export const LimitItem = memo((props: Props) => {
    const {
        limitId,
        limitDictionary,
        onChangeLimit,
        onClearTemporaryLimit,
        onClearCustomLimit,

        usedValue,
        defaultValue,
        customValue,
        tempValue,
        untilDate,

        readonly,
        labelExtra,
    } = props;

    const {locale} = useContext(LocaleContext);

    const value = useMemo(() => {
        if (tempValue !== undefined) {
            if (tempValue === MAX_NUMBER_VALUE) {
                return undefined;
            } else {
                return tempValue;
            }
        }

        if (customValue !== undefined) {
            if (customValue === MAX_NUMBER_VALUE) {
                return undefined;
            } else {
                return customValue;
            }
        }

        if (defaultValue === MAX_NUMBER_VALUE) {
            return undefined;
        } else {
            return defaultValue;
        }
    }, [tempValue, customValue, defaultValue]);

    const displayedValue = useMemo(() => {
        const formattedValue = value === MAX_NUMBER_VALUE || value === undefined
            ? 'Unlimited'
            : Integer.format(value, locale);
        const formattedUsedValue = usedValue ? ` (${Integer.format(usedValue, locale)} used)` : '';

        return formattedValue + formattedUsedValue;
    }, [value, usedValue, locale]);

    return (
        <NumberInput
            id={`limit-${limitId}`}
            labelId={`limit-${limitId}-label`}
            value={value}
            width="100%"
            onBlur={newValue => {
                if (newValue !== value) {
                    onChangeLimit(limitDictionary.code, newValue);
                }
            }}
            displayedValue={displayedValue}
            label={(
                <LimitItemLabel
                    limitId={limitId}
                    limitDictionary={limitDictionary}
                    onClearTemporaryLimit={onClearTemporaryLimit}
                    onClearCustomLimit={onClearCustomLimit}
                    defaultValue={defaultValue}
                    customValue={customValue}
                    tempValue={tempValue}
                    untilDate={untilDate}
                    labelExtra={labelExtra}
                />
            )}
            readOnly={readonly}
            maxValue={99999999}
        />
    );
});
